import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Container, Table, Button, ButtonGroup, Form, FormControl, FormGroup, FormLabel, Modal, ListGroup } from 'react-bootstrap';
import { AiOutlineClose, AiOutlineEdit } from 'react-icons/ai';

const QuestionsManager = () => {
    const [roles, setRoles] = useState([]);
    const [selectedRole, setSelectedRole] = useState('Buyer');
    const [question, setQuestion] = useState('');
    const [questions, setQuestions] = useState([]);
    const [modalIsOpen, setIsOpen] = useState(false);
    const [editModalIsOpen, setEditModalIsOpen] = useState(false);
    const [optionEditModalIsOpen, setOptionEditModalIsOpen] = useState(false);
    const [currentQuestionId, setCurrentQuestionId] = useState(null);
    const [options, setOptions] = useState([{ value: '' }]);
    const [questionOptions, setQuestionOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [currentOptionId, setCurrentOptionId] = useState(null);
    const [currentOptionValue, setCurrentOptionValue] = useState('');
    const [newOptionValue, setNewOptionValue] = useState('');

    const [currentQuestion, setCurrentQuestion] = useState({ id: '', user_type: '', question: '' });

    useEffect(() => {
        refreshQuestions();
        axios.get('https://api.biznetusa.com/api/get-roles')
            .then(response => {
                if (response.data.status === 200) {
                    setRoles(response.data.roles);
                }
            })
            .catch(() => {
                setError('Failed to fetch roles.');
            });
    }, [selectedRole]);

    const handleRoleChange = (e) => {
        const value = e.target.value;
        setSelectedRole(value)
    }

    const handleFetchOptions = async (questionID) => {
        try {
            const response = await axios.get(`https://api.biznetusa.com/api/get-question-options/${questionID}`)
            setQuestionOptions(response.data.question_options);
            console.log("Successfully fetched options: ", response.data.question_options);
        } catch (err) {
            console.error('Failed to fetch options: ', err);
        }
    }

    const handleOptionDelete = async (optionID) => {
        if (window.confirm('Are you sure you want to delete this question?')) {
            setLoading(true);
            try {
                const response = await axios.delete(`https://api.biznetusa.com/api/deletequestionoptions/${optionID}`);
                if (response.data.status === 200) {
                    alert('Option deleted successfully!');
                    refreshQuestions();
                    handleFetchOptions(currentQuestionId)
                } else {
                    alert('Failed to delete question: ' + (response.data.message || 'Unknown error'));
                }
            } catch (error) {
                alert('Error deleting question: ' + error.message);
            } finally {
                setLoading(false);
            }
        }
    }

    const handleEditOption = (option) => {
        setCurrentOptionId(option.id);
        setCurrentOptionValue(option.option_value);
        setOptionEditModalIsOpen(true);
    };

    const handleSaveOptionEdit = async () => {
        if (!currentOptionValue.trim()) {
            alert('Option value cannot be empty.');
            return;
        }
        setLoading(true);
        try {
            const response = await axios.put(`https://api.biznetusa.com/api/update-questionoptions/${currentOptionId}`, {
                question_id: currentQuestionId,
                option_value: currentOptionValue,
            });

            if (response.data.status === 200) {
                alert('Option updated successfully!');
                closeEditModal();
                refreshQuestions();
                handleFetchOptions(currentQuestionId)
            } else {
                alert('Failed to update option.');
            }
        } catch (error) {
            alert('Error updating option: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const openModal = (questionId) => {
        handleFetchOptions(questionId);
        setCurrentQuestionId(questionId);
        setIsOpen(true);
    };

    const closeModal = () => {
        setIsOpen(false);
        setOptions([{ value: '' }]); // Reset options on modal close
    };

    const openEditModal = (question) => {
        setCurrentQuestion({ id: question.id, user_type: question.user_type, question: question.question });
        setEditModalIsOpen(true);
    };

    const closeEditModal = () => {
        setEditModalIsOpen(false);
        setCurrentQuestion({ id: '', user_type: '', question: '' });
    };

    const closeOptionEditModal = () => {
        setOptionEditModalIsOpen(false);
        setCurrentOptionId(null);
        setCurrentOptionValue('');
    };

    const handleSaveQuestionEdit = async () => {
        const { id, user_type, question } = currentQuestion;

        if (!user_type || !question.trim()) {
            alert('Both fields are required.');
            return;
        }

        setLoading(true);
        try {
            const response = await axios.put(`https://api.biznetusa.com/api/register-question/${id}`, {
                user_type,
                question,
            });

            if (response.data.status === 200) {
                alert('Question updated successfully!');
                refreshQuestions();
                closeEditModal();
            } else {
                alert('Failed to update question: ' + (response.data.message || 'Unknown error'));
            }
        } catch (error) {
            alert('Error updating question: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAddQuestion = async (event) => {
        event.preventDefault();
        setLoading(true);
        try {
            const response = await axios.post('https://api.biznetusa.com/api/store-questions', {
                user_type: selectedRole,
                question: question,
            });
            if (response.data.status === 200 && response.data.question_id) {
                alert('Question added successfully!');
                setQuestion('');
            } else {
                alert('Failed to add question: ' + (response.data.message || 'Unknown error'));
            }
        } catch (error) {
            alert('Error adding question: ' + error.message);
        } finally {
            setLoading(false);
            refreshQuestions();
        }
    };

    const handleAddOption = async () => {
        if (!newOptionValue.trim()) {
            alert('Option value cannot be empty.');
            return;
        }
        setLoading(true);
        try {
            const response = await axios.post(`https://api.biznetusa.com/api/store-questionoptions`, {
                question_id: currentQuestionId,
                option_value: newOptionValue,
            });

            if (response.data.status === 200) {
                alert('Option added successfully!');
                setNewOptionValue('');
                handleFetchOptions(currentQuestionId); // Refresh options
            } else {
                alert('Failed to add option.');
            }
        } catch (error) {
            alert('Error adding option: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteQuestion = async (questionId) => {
        if (window.confirm('Are you sure you want to delete this question?')) {
            setLoading(true);
            try {
                const response = await axios.delete(`https://api.biznetusa.com/api/deletequestions/${questionId}`);
                if (response.data.status === 200) {
                    alert('Question deleted successfully!');
                    refreshQuestions();
                } else {
                    alert('Failed to delete question: ' + (response.data.message || 'Unknown error'));
                }
            } catch (error) {
                alert('Error deleting question: ' + error.message);
            } finally {
                setLoading(false);
            }
        }
    };


    const refreshQuestions = () => {
        axios.get(`https://api.biznetusa.com/api/get-questions/${selectedRole}`)
            .then(response => {
                if (response.data.status === 200) {
                    setQuestions(response.data.questions);
                }
            });
    };

    return (
        <Container className="mt-4">
            <h2>Manage Questions</h2>
            {error && <div className="alert alert-danger">{error}</div>}
            <Form onSubmit={handleAddQuestion}>
                <FormGroup>
                    <FormLabel>User Type</FormLabel>
                    <FormControl as="select" value={selectedRole} onChange={handleRoleChange}>
                        <option value="">Select User Type</option>
                        {roles.map(role => (
                            <option key={role.id} value={role.role_name}>{role.role_name}</option>
                        ))}
                    </FormControl>
                </FormGroup>
                <FormGroup className="mt-3">
                    <FormLabel>Question</FormLabel>
                    <FormControl
                        type="text"
                        value={question}
                        onChange={e => setQuestion(e.target.value)}
                        placeholder="Enter your question"
                    />
                </FormGroup>
                <Button type="submit" disabled={loading} className="mt-3">
                    {loading ? 'Adding...' : 'Add Question'}
                </Button>
            </Form>

            <Table striped bordered hover className="mt-3">
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>User Type</th>
                        <th>Question</th>
                        <th>Options</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {questions.map(q => (
                        <tr key={q.id}>
                            <td>{q.id}</td>
                            <td>{q.user_type}</td>
                            <td>{q.question}</td>
                            <td>{q.options.map(o => o.option_value).join(', ')}</td>
                            <td>
                                <Button onClick={() => openModal(q.id)} variant="primary" className="mb-2">Options</Button>
                                <br />
                                <Button onClick={() => openEditModal(q)} variant="primary" className="mb-2">Edit</Button>
                                <Button variant="danger" onClick={() => handleDeleteQuestion(q.id)} className="ml-2">Delete</Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>

            <Modal show={modalIsOpen} onHide={closeModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Options</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <FormGroup>
                        <FormControl
                            type="text"
                            value={newOptionValue}
                            onChange={(e) => setNewOptionValue(e.target.value)}
                            placeholder="Enter new option"
                        />
                    </FormGroup>
                    <ListGroup className="my-2">
                        {questionOptions.map(item => (
                            <ListGroup.Item key={item.id} className="d-flex justify-content-between align-items-center">
                                <span>{item.option_value}</span>
                                <ButtonGroup>
                                    <Button variant="primary" onClick={() => handleEditOption(item)}>
                                        <AiOutlineEdit />
                                    </Button>
                                    <Button variant="danger" onClick={() => handleOptionDelete(item.id)}>
                                        <AiOutlineClose />
                                    </Button>
                                </ButtonGroup>
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                    <Button variant="success" onClick={handleAddOption} disabled={loading}>
                        {loading ? 'Adding...' : 'Add Option'}
                    </Button>
                </Modal.Body>
            </Modal>

            <Modal show={editModalIsOpen} onHide={closeEditModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Question</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <FormGroup>
                            <FormLabel>User Type</FormLabel>
                            <FormControl
                                as="select"
                                value={currentQuestion.user_type}
                                onChange={(e) => setCurrentQuestion({ ...currentQuestion, user_type: e.target.value })}
                            >
                                <option value="">Select User Type</option>
                                {roles.map(role => (
                                    <option key={role.id} value={role.role_name}>{role.role_name}</option>
                                ))}
                            </FormControl>
                        </FormGroup>
                        <FormGroup className="mt-3">
                            <FormLabel>Question</FormLabel>
                            <FormControl
                                type="text"
                                value={currentQuestion.question}
                                onChange={(e) => setCurrentQuestion({ ...currentQuestion, question: e.target.value })}
                                placeholder="Enter your question"
                            />
                        </FormGroup>
                        <Button variant="primary" onClick={handleSaveQuestionEdit} disabled={loading} className="mt-3">
                            {loading ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Edit Option Modal */}
            <Modal show={optionEditModalIsOpen} onHide={closeOptionEditModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Option</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <FormGroup>
                            <FormLabel>Option Value</FormLabel>
                            <FormControl
                                type="text"
                                value={currentOptionValue}
                                onChange={(e) => setCurrentOptionValue(e.target.value)}
                                placeholder="Edit option"
                            />
                        </FormGroup>
                        <Button variant="primary" onClick={handleSaveOptionEdit} disabled={loading} className="mt-3">
                            {loading ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </Form>
                </Modal.Body>
            </Modal>
        </Container >
    );
};

export default QuestionsManager;

